/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Input from "../../../components/Input"; 
import toast from "react-hot-toast";
import { IconDeviceFloppy, IconEdit, IconLoader2 } from "@tabler/icons-react";
import { useUpdatePassword } from "../../../apis/settingsApis";

type FormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export default function Password() {
  const [isEdit, setIsEdit] = useState(false);
  const { mutate, isPending } = useUpdatePassword();

  // Example: data fetched from API (replace with your API call)
  const fetchedData = {
    currentPassword: "", // <-- never comes from API in real-world
    newPassword: "",
    confirmPassword: "",
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: fetchedData, // prefill when form mounts
  });

  const handleUpdatePassword = (data: FormValues) => {
    mutate(data, {
      onSuccess: () => {
        toast.success("Password updated successfully!");
        setIsEdit(false);
        reset(data); // keep last entered values prefilled
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(error?.message || "Failed to update password");
      },
    });
  };

  const newPassword = watch("newPassword");

  useEffect(() => {
    if (isEdit) {
      // prefill with fetched data whenever edit mode is enabled
      reset(fetchedData);
    }
  }, [isEdit, reset]);

  return (
    <div className="divide-y divide-[#212121]/10 overflow-hidden">
      {/* Current Password */}
      <Input
        name="currentPassword"
        type="password"
        title="Current Password"
        placeholder=" "
        register={register}
        error={errors.currentPassword}
        validation={{ required: "Current Password is required" }}
        inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
          isEdit
            ? "bg-white border border-[#D8D8D8]"
            : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
        }`}
        labelClass="w-[50%]"
        className="flex justify-between items-center py-4"
        disabled={!isEdit}
      />

      {/* New Password */}
      <Input
        name="newPassword"
        type="password"
        title="New Password"
        placeholder=" "
        register={register}
        error={errors.newPassword}
        validation={{
          required: "New Password is required",
          minLength: {
            value: 8,
            message: "Password must be at least 8 characters",
          },
          validate: {
            hasUppercase: (v) =>
              /[A-Z]/.test(v) || "Must contain at least one uppercase letter",
            hasLowercase: (v) =>
              /[a-z]/.test(v) || "Must contain at least one lowercase letter",
            hasNumber: (v) =>
              /[0-9]/.test(v) || "Must contain at least one number",
          },
        }}
        inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
          isEdit
            ? "bg-white border border-[#D8D8D8]"
            : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
        }`}
        labelClass="w-[50%]"
        className="flex justify-between items-center py-4"
        disabled={!isEdit}
      />

      {/* Confirm Password */}
      <Input
        name="confirmPassword"
        type="password"
        title="Confirm Password"
        placeholder=" "
        register={register}
        error={errors.confirmPassword}
        validation={{
          required: "Confirm Password is required",
          validate: (value) =>
            value === newPassword || "Passwords do not match",
        }}
        inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
          isEdit
            ? "bg-white border border-[#D8D8D8]"
            : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
        }`}
        labelClass="w-[50%]"
        className="flex justify-between items-center py-4"
        disabled={!isEdit}
      />

      {/* Note */}
      <div className="py-3">
        <p className="text-sm px-2 text-[#212121] border-l-4 border-[#FBD037]">
          <b>Note</b>: Minimum 8 characters, must include one number, one
          uppercase, and one lowercase character.
        </p>
      </div>

      {/* Save / Edit Button */}
      <button
        onClick={() =>
          isEdit ? handleSubmit(handleUpdatePassword)() : setIsEdit(true)
        }
        disabled={isPending}
        className="bg-[#FBD037] text-[#212121] text-base font-semibold p-2.5 rounded-full flex items-center justify-center"
      >
        {isPending ? (
          <IconLoader2 className="animate-spin" />
        ) : isEdit ? (
          <IconDeviceFloppy />
        ) : (
          <IconEdit />
        )}
      </button>
    </div>
  );
}
